/**
 * Dynamic Price Update Component
 * Updates product price display when quantity changes
 * Works with B2B app price elements
 */
if (!customElements.get('dynamic-price-update')) {
  customElements.define(
    'dynamic-price-update',
    class DynamicPriceUpdate extends HTMLElement {
      constructor() {
        super();
        this.quantityInput = null;
        this.originalPriceElement = null;
        this.currentPriceElement = null;
        this.originalPriceSpan = null;
        this.currentPriceSpan = null;
        this.unitPriceSpan = null;
        this.quantity = 1;
        this.unitPrice = 0;
        this.unitPriceExclVat = 0;
        this.unitPriceInclVat = 0;
        this.currentPrice = 0;
        this.originalPrice = 0;
        this.compareAtPriceExclVat = 0; // Store excl. BTW compare price for recalculation
        this.isB2B = false;
        this.baseUnitPrice = 0; // Store base unit price before discount
        this.currentDiscount = 0; // Current discount percentage (0-100)
        this.quantityBreakTable = null; // Reference to the quantity break table
        this.cartUnitPriceFromDynamicElement = 0; // Store unit price from dynamic-price element under title (for cart total calculation)
      }

      connectedCallback() {
        console.log('[DynamicPriceUpdate] connectedCallback - element connected to DOM');
        this.init();
      }

      init() {
        console.log('[DynamicPriceUpdate] init() called');
        // Find the original B2B price element - look in parent or nearby
        const isCart = this.hasAttribute('data-is-cart') || 
                      this.getAttribute('data-is-cart') === 'true';
        const isCartTotal = this.hasAttribute('data-is-cart-total') || 
                           this.getAttribute('data-is-cart-total') === 'true';
        const showInclOnly = this.hasAttribute('data-show-incl-only') || 
                           this.getAttribute('data-show-incl-only') === 'true';
        
        if (isCartTotal) {
          // For cart total, look for bss-b2b-cart-total-price nearby
          const totalsDiv = this.closest('.totals') || this.parentElement;
          if (totalsDiv) {
            const cartTotalPriceEl = totalsDiv.querySelector('[bss-b2b-cart-total-price]');
            if (cartTotalPriceEl) {
              this.originalPriceElement = cartTotalPriceEl;
              // Mark it for consistency
              if (!this.originalPriceElement.hasAttribute('bss-b2b-product-parent-price')) {
                this.originalPriceElement.setAttribute('bss-b2b-product-parent-price', 'true');
              }
            }
          }
        } else if (isCart) {
          // Check if this is the price under title (should use unit price, not total)
          // showInclOnly is already defined at the beginning of init()
          
          if (showInclOnly) {
            // For price under title, look for the product price element (unit price)
            // This is in the cart-item__price-under-title div
            const priceUnderTitleDiv = this.closest('.cart-item__price-under-title');
            if (priceUnderTitleDiv) {
              const productPriceEl = priceUnderTitleDiv.querySelector('[bss-b2b-product-price]');
              if (productPriceEl) {
                // Use the parent span as the price element
                this.originalPriceElement = productPriceEl.closest('[bss-b2b-product-parent-price="true"]') || productPriceEl.parentElement;
                // Mark it for consistency
                if (this.originalPriceElement && !this.originalPriceElement.hasAttribute('bss-b2b-product-parent-price')) {
                  this.originalPriceElement.setAttribute('bss-b2b-product-parent-price', 'true');
                }
              }
            }
          } else {
            // For cart total price column, we need to get the UNIT price
            // The B2B app's product-price might have old prices (before staffelkorting)
            // So we should get the unit price from the dynamic-price-update element under the title
            // which has the correct price after staffelkorting
            const cartRow = this.closest('tr') || this.closest('.cart-item');
            if (cartRow) {
            // First try to get unit price from the dynamic-price-update element under title
            // This has the correct price after staffelkorting
            // The B2B app injects prices into the dynamic-price__main div
            const priceUnderTitleElement = cartRow.querySelector('dynamic-price-update[data-show-incl-only="true"]');
            if (priceUnderTitleElement) {
              // Try to get price from .dynamic-price__current first
              let unitPriceSpan = priceUnderTitleElement.querySelector('.dynamic-price__current');
              let priceText = '';
              
              if (unitPriceSpan && unitPriceSpan.textContent && unitPriceSpan.textContent.trim()) {
                priceText = unitPriceSpan.textContent.trim();
              } else {
                // Fallback: get price from B2B app elements within the dynamic-price element
                // The B2B app injects prices into .dynamic-price__main
                const mainDiv = priceUnderTitleElement.querySelector('.dynamic-price__main');
                if (mainDiv) {
                  // Look for B2B price elements
                  const b2bPriceExcl = mainDiv.querySelector('.bss-b2b-price-no-vat');
                  const b2bPriceIncl = mainDiv.querySelector('.bss-b2b-price-has-vat');
                  
                  // Use the one that matches B2B status (we'll detect this later)
                  // For now, try excl first (most common for B2B)
                  if (b2bPriceExcl && b2bPriceExcl.textContent) {
                    priceText = b2bPriceExcl.textContent.trim();
                    console.log('[DynamicPriceUpdate] Cart total - found B2B price in dynamic element (excl):', priceText);
                  } else if (b2bPriceIncl && b2bPriceIncl.textContent) {
                    priceText = b2bPriceIncl.textContent.trim();
                    console.log('[DynamicPriceUpdate] Cart total - found B2B price in dynamic element (incl):', priceText);
                  }
                }
              }
              
              if (priceText) {
                // Extract price from text (e.g., "€2,66 excl. BTW" or "€2,66")
                const priceMatch = priceText.match(/€?\s*([\d,\.]+)/);
                if (priceMatch) {
                  let priceString = priceMatch[1];
                  if (priceString.includes('.') && priceString.includes(',')) {
                    priceString = priceString.replace(/\./g, '').replace(',', '.');
                  } else {
                    priceString = priceString.replace(',', '.');
                  }
                  const extractedUnitPrice = parseFloat(priceString) || 0;
                  if (extractedUnitPrice > 0) {
                    // Store this as the unit price for calculation
                    // We'll use this in renderPrice to calculate total
                    this.cartUnitPriceFromDynamicElement = extractedUnitPrice;
                    console.log('[DynamicPriceUpdate] Cart total - using unit price from dynamic-price element under title:', extractedUnitPrice);
                    
                    // Still need originalPriceElement for B2B detection, use product-price
                    const productPriceEl = cartRow.querySelector('[bss-b2b-product-price]');
                    if (productPriceEl) {
                      this.originalPriceElement = productPriceEl.closest('[bss-b2b-product-parent-price="true"]') || productPriceEl.parentElement;
                      if (this.originalPriceElement && !this.originalPriceElement.hasAttribute('bss-b2b-product-parent-price')) {
                        this.originalPriceElement.setAttribute('bss-b2b-product-parent-price', 'true');
                      }
                    }
                  }
                }
              }
            }
              
              // If we didn't get price from dynamic element, fallback to product-price
              if (!this.cartUnitPriceFromDynamicElement) {
                const productPriceEl = cartRow.querySelector('[bss-b2b-product-price]');
                if (productPriceEl) {
                  this.originalPriceElement = productPriceEl.closest('[bss-b2b-product-parent-price="true"]') || productPriceEl.parentElement;
                  if (this.originalPriceElement && !this.originalPriceElement.hasAttribute('bss-b2b-product-parent-price')) {
                    this.originalPriceElement.setAttribute('bss-b2b-product-parent-price', 'true');
                  }
                  console.log('[DynamicPriceUpdate] Cart total - using product-price (unit price) instead of final-line-price');
                } else {
                  // Last fallback: use final-line-price if product-price not found
                  const cartCell = this.closest('td') || this.parentElement;
                  if (cartCell) {
                    const finalLinePriceEl = cartCell.querySelector('[bss-b2b-final-line-price]');
                    if (finalLinePriceEl) {
                      this.originalPriceElement = finalLinePriceEl.parentElement || finalLinePriceEl;
                      if (!this.originalPriceElement.hasAttribute('bss-b2b-product-parent-price')) {
                        this.originalPriceElement.setAttribute('bss-b2b-product-parent-price', 'true');
                      }
                      console.warn('[DynamicPriceUpdate] Cart total - fallback to final-line-price (may have rounding errors)');
                    }
                  }
                }
              }
            }
          }
        }
        
        // First try to find it in the same parent block (for main product pages only)
        if (!this.originalPriceElement) {
          // Check if we're in the main product info area (not in a product card)
          const isInMainProduct = this.closest('#ProductInfo-') || 
                                 this.closest('.product__info-container') ||
                                 this.closest('.product__block--price');
          
          if (isInMainProduct) {
            let parentBlock = this.closest('.product__block--price');
            if (parentBlock) {
              this.originalPriceElement = parentBlock.querySelector('[bss-b2b-product-parent-price="true"]');
            }
          }
        }
        
        // Fallback: search in nearby elements, but only within the same product card
        if (!this.originalPriceElement) {
          // Check if we're inside a product card (for recommended/complementary products)
          const productCard = this.closest('.product-card') || 
                            this.closest('.f-column') ||
                            this.closest('.swiper-slide');
          
          if (productCard) {
            // Only search within this product card
            this.originalPriceElement = productCard.querySelector('[bss-b2b-product-parent-price="true"]');
          } else {
            // For main product page, try to find nearby
            const nearby = this.previousElementSibling || this.parentElement;
            if (nearby) {
              this.originalPriceElement = nearby.querySelector('[bss-b2b-product-parent-price="true"]') ||
                                         nearby.querySelector('[bss-b2b-final-line-price]')?.parentElement;
            }
          }
          
          // Last resort: only search entire document if we're on the main product page (not in a card)
          if (!this.originalPriceElement && !productCard) {
            // Only search in main product info area, not in complementary products
            const mainProductInfo = document.querySelector('#ProductInfo-') || 
                                  document.querySelector('.product__info-container') ||
                                  document.querySelector('.product__block--price');
            if (mainProductInfo) {
              this.originalPriceElement = mainProductInfo.querySelector('[bss-b2b-product-parent-price="true"]');
            }
          }
        }
        
        if (!this.originalPriceElement) {
          console.warn('DynamicPriceUpdate: B2B price element not found');
          return;
        }

        // Hide the original price element's content but keep the element for B2B app
        const priceDisplayElement = this.originalPriceElement.querySelector('[bss-b2b-product-price]') || 
                                   this.originalPriceElement.querySelector('[id*="price"]');
        if (priceDisplayElement) {
          priceDisplayElement.style.visibility = 'hidden';
          priceDisplayElement.style.position = 'absolute';
          priceDisplayElement.style.opacity = '0';
        }

        // Check if this is on a collection page (no quantity input needed)
        const isCollection = this.hasAttribute('data-is-collection') || 
                            this.getAttribute('data-is-collection') === 'true';
        
        // For cart items, get quantity from the cart item
        if (isCart && !isCartTotal) {
          const itemKey = this.getAttribute('data-item-key');
          if (itemKey) {
            // Find the quantity input for this cart item
            const cartRow = this.closest('tr') || this.closest('.cart-item');
            if (cartRow) {
              const quantityInput = cartRow.querySelector('input[name="updates[]"]');
              if (quantityInput) {
                this.quantityInput = quantityInput;
                this.quantity = parseInt(quantityInput.value) || 1;
              } else {
                // Fallback: quantity might be in a different structure
                this.quantity = 1; // Default to 1 if not found
              }
            } else {
              this.quantity = 1;
            }
          } else {
            this.quantity = 1;
          }
        } else if (!isCollection && !isCart && !isCartTotal) {
          // Find quantity input (only on product pages)
          const sectionId = this.getAttribute('data-section-id');
          if (sectionId) {
            this.quantityInput = document.getElementById(`Quantity-${sectionId}`);
          } else {
            // Fallback: find any quantity input in the product form
            const productForm = document.querySelector('form[is="product-form"]');
            if (productForm) {
              this.quantityInput = productForm.querySelector('input[name="quantity"]');
            }
          }

          if (this.quantityInput) {
            // Set initial quantity
            this.quantity = parseInt(this.quantityInput.value) || 1;

            // Listen to quantity changes
            this.quantityInput.addEventListener('change', this.handleQuantityChange.bind(this));
            
            // Also listen to input events for real-time updates
            this.quantityInput.addEventListener('input', this.handleQuantityChange.bind(this));
          }
        } else {
          // On collection pages, cart total, and cart items, quantity is always 1
          if (isCartTotal) {
            // Cart total doesn't need quantity
            this.quantity = 1;
          } else {
            this.quantity = 1;
          }
        }

        // Listen to variant changes (if variant changes, price might change)
        if (window.FoxTheme && window.FoxTheme.pubsub) {
          this.variantChangeUnsubscriber = window.FoxTheme.pubsub.subscribe(
            window.FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            this.handleVariantChange.bind(this)
          );
        }

        // Initial render (with delay to let B2B app initialize)
        // Longer delay for collection pages, cart, and cart total as B2B app might load later
        const delay = (isCollection || isCart || isCartTotal) ? 1000 : 500;
        
        // Try multiple times to catch B2B app updates
        // For cart items, we need more retries as B2B app might be very slow
        const retries = isCart ? 6 : 3;
        const retryInterval = isCart ? 2000 : 1000;
        
        for (let i = 0; i < retries; i++) {
          setTimeout(() => {
            console.log(`[DynamicPriceUpdate] Retry ${i + 1}/${retries} - updating prices (delay: ${delay + (i * retryInterval)}ms)`);
            this.updatePriceFromB2BElement();
            this.renderPrice();
            
            // Log current state
            if (i === retries - 1) {
              console.log('[DynamicPriceUpdate] Final state after all retries:', {
                unitPrice: this.unitPrice,
                unitPriceExclVat: this.unitPriceExclVat,
                unitPriceInclVat: this.unitPriceInclVat,
                currentPrice: this.currentPrice,
                originalPrice: this.originalPrice,
                originalPriceElement: this.originalPriceElement,
                originalPriceElementHTML: this.originalPriceElement?.innerHTML?.substring(0, 200)
              });
            }
          }, delay + (i * retryInterval));
        }

        // Start observing B2B price changes
        this.observeB2BPriceChanges();
        
        // Initialize quantity break table handling (for staffelkorting)
        this.initQuantityBreakTable();
        
        // For cart total price, observe the dynamic-price element under title for updates
        if (isCart && !showInclOnly) {
          this.observeCartUnitPriceElement();
        }
      }

      disconnectedCallback() {
        if (this.quantityInput) {
          this.quantityInput.removeEventListener('change', this.handleQuantityChange);
          this.quantityInput.removeEventListener('input', this.handleQuantityChange);
        }
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
        if (this.priceObserver) {
          this.priceObserver.disconnect();
        }
      }

      updatePriceFromB2BElement() {
        console.log('[DynamicPriceUpdate] updatePriceFromB2BElement called');
        if (!this.originalPriceElement) {
          console.warn('[DynamicPriceUpdate] No originalPriceElement found, returning');
          return;
        }
        console.log('[DynamicPriceUpdate] originalPriceElement found:', this.originalPriceElement);

        // Check what type of element this is
        const isCart = this.hasAttribute('data-is-cart') || 
                      this.getAttribute('data-is-cart') === 'true';
        const isCartTotal = this.hasAttribute('data-is-cart-total') || 
                           this.getAttribute('data-is-cart-total') === 'true';
        
        let priceExclVatElement, priceInclVatElement;
        
        if (isCartTotal) {
          // For cart total, look in bss-b2b-cart-total-price element
          const cartTotalPriceElement = this.originalPriceElement.querySelector('[bss-b2b-cart-total-price]') ||
                                       this.originalPriceElement;
          priceExclVatElement = cartTotalPriceElement.querySelector('.bss-b2b-price-no-vat');
          priceInclVatElement = cartTotalPriceElement.querySelector('.bss-b2b-price-has-vat');
        } else if (isCart) {
          // For cart items, check if we're looking at total price or unit price
          const showInclOnly = this.hasAttribute('data-show-incl-only') || 
                             this.getAttribute('data-show-incl-only') === 'true';
          
          if (showInclOnly) {
            // Price under title: use product-price (unit price)
            const productPriceElement = this.originalPriceElement?.querySelector('[bss-b2b-product-price]') ||
                                       this.originalPriceElement;
            priceExclVatElement = productPriceElement?.querySelector('.bss-b2b-price-no-vat');
            priceInclVatElement = productPriceElement?.querySelector('.bss-b2b-price-has-vat');
            console.log('[DynamicPriceUpdate] Cart item - price under title (unit price)');
          } else {
            // Total price column: use product-price (unit price), NOT final-line-price
            // The final-line-price has rounding errors, so we'll calculate total ourselves
            const productPriceElement = this.originalPriceElement?.querySelector('[bss-b2b-product-price]') ||
                                       this.originalPriceElement;
            priceExclVatElement = productPriceElement?.querySelector('.bss-b2b-price-no-vat');
            priceInclVatElement = productPriceElement?.querySelector('.bss-b2b-price-has-vat');
            console.log('[DynamicPriceUpdate] Cart item - total price column (using unit price from product-price)');
            
            // If not found in current element, search in cart row
            if (!priceExclVatElement && !priceInclVatElement) {
              const cartRow = this.closest('tr') || this.closest('.cart-item');
              if (cartRow) {
                const productPriceEl = cartRow.querySelector('[bss-b2b-product-price]');
                if (productPriceEl) {
                  priceExclVatElement = productPriceEl.querySelector('.bss-b2b-price-no-vat');
                  priceInclVatElement = productPriceEl.querySelector('.bss-b2b-price-has-vat');
                  console.log('[DynamicPriceUpdate] Cart item - found product-price in cart row');
                }
              }
            }
          }
          
          console.log('[DynamicPriceUpdate] Cart item - priceExclVatElement:', priceExclVatElement);
          console.log('[DynamicPriceUpdate] Cart item - priceInclVatElement:', priceInclVatElement);
        } else {
          // Find both price elements (excl. and incl. BTW)
          priceExclVatElement = this.originalPriceElement.querySelector('.bss-b2b-price-no-vat');
          priceInclVatElement = this.originalPriceElement.querySelector('.bss-b2b-price-has-vat');
        }
        
        // CRITICAL: FIRST determine if user is B2B (excl. BTW price is visible/larger)
        // This MUST be done BEFORE calculating originalPrice from compare_at_price
        this.isB2B = false;
        if (priceExclVatElement && priceInclVatElement) {
          const exclVatStyle = window.getComputedStyle(priceExclVatElement);
          const inclVatStyle = window.getComputedStyle(priceInclVatElement);
          
          // Check font sizes - B2B users see excl. BTW with larger font
          const exclSize = parseFloat(exclVatStyle.fontSize);
          const inclSize = parseFloat(inclVatStyle.fontSize);
          
          // Also check visibility
          const exclVisible = exclVatStyle.visibility !== 'hidden' && 
                             exclVatStyle.display !== 'none' && 
                             exclVatStyle.opacity !== '0';
          const inclVisible = inclVatStyle.visibility !== 'hidden' && 
                             inclVatStyle.display !== 'none' && 
                             inclVatStyle.opacity !== '0';
          
          // If excl. BTW has larger or equal font size and is visible, user is B2B
          if (exclSize >= inclSize && exclVisible) {
            this.isB2B = true;
            console.log('[DynamicPriceUpdate] B2B detected (exclSize >= inclSize):', exclSize, '>=', inclSize);
          } else if (!inclVisible && exclVisible) {
            // If only excl. is visible, user is B2B
            this.isB2B = true;
            console.log('[DynamicPriceUpdate] B2B detected (only excl visible)');
          } else {
            console.log('[DynamicPriceUpdate] Regular customer detected (exclSize:', exclSize, 'inclSize:', inclSize, 'exclVisible:', exclVisible, 'inclVisible:', inclVisible);
          }
        } else if (priceExclVatElement) {
          // Only excl. BTW element exists, check if visible
          const exclVatStyle = window.getComputedStyle(priceExclVatElement);
          const exclVisible = exclVatStyle.visibility !== 'hidden' && 
                             exclVatStyle.display !== 'none' && 
                             exclVatStyle.opacity !== '0';
          if (exclVisible) {
            this.isB2B = true;
            console.log('[DynamicPriceUpdate] B2B detected (only excl element exists and visible)');
          } else {
            console.log('[DynamicPriceUpdate] Regular customer (excl element not visible)');
          }
        }

        // Extract excl. BTW price
        if (priceExclVatElement) {
          const priceText = priceExclVatElement.textContent || priceExclVatElement.innerText || '';
          console.log('[DynamicPriceUpdate] Extracting excl. BTW price from text:', priceText);
          const priceMatch = priceText.match(/€?\s*([\d,\.]+)/);
          if (priceMatch) {
            let priceString = priceMatch[1];
            if (priceString.includes('.') && priceString.includes(',')) {
              priceString = priceString.replace(/\./g, '').replace(',', '.');
            } else {
              priceString = priceString.replace(',', '.');
            }
            this.unitPriceExclVat = parseFloat(priceString) || 0;
            console.log('[DynamicPriceUpdate] Extracted unitPriceExclVat:', this.unitPriceExclVat);
          } else {
            console.warn('[DynamicPriceUpdate] Could not match price pattern in excl. BTW text:', priceText);
          }
        } else {
          console.log('[DynamicPriceUpdate] No priceExclVatElement found');
        }

        // Extract incl. BTW price
        if (priceInclVatElement) {
          const priceText = priceInclVatElement.textContent || priceInclVatElement.innerText || '';
          console.log('[DynamicPriceUpdate] Extracting incl. BTW price from text:', priceText);
          const priceMatch = priceText.match(/€?\s*([\d,\.]+)/);
          if (priceMatch) {
            let priceString = priceMatch[1];
            if (priceString.includes('.') && priceString.includes(',')) {
              priceString = priceString.replace(/\./g, '').replace(',', '.');
            } else {
              priceString = priceString.replace(',', '.');
            }
            this.unitPriceInclVat = parseFloat(priceString) || 0;
            console.log('[DynamicPriceUpdate] Extracted unitPriceInclVat:', this.unitPriceInclVat);
          } else {
            console.warn('[DynamicPriceUpdate] Could not match price pattern in incl. BTW text:', priceText);
          }
        } else {
          console.log('[DynamicPriceUpdate] No priceInclVatElement found');
        }

        // Store base price before applying any discounts (only on first load)
        if (!this.baseUnitPrice || this.baseUnitPrice === 0) {
          if (this.unitPriceExclVat > 0) {
            this.baseUnitPrice = this.unitPriceExclVat;
          } else if (this.unitPriceInclVat > 0) {
            this.baseUnitPrice = this.unitPriceInclVat / 1.21; // Convert incl. to excl. for base
          } else if (this.unitPrice > 0) {
            this.baseUnitPrice = this.isB2B ? this.unitPrice : this.unitPrice / 1.21;
          }
        }
        
        // Set unit price based on B2B status
        // Use the price that matches the user type (excl. for B2B, incl. for regular)
        // IMPORTANT: For cart items (not price under title), the price from final-line-price is already the TOTAL
        // So we need to store it differently
        // isCart and isCartTotal are already defined at the beginning of this function
        const showInclOnly = this.hasAttribute('data-show-incl-only') || 
                           this.getAttribute('data-show-incl-only') === 'true';
        
        console.log('[DynamicPriceUpdate] Setting unitPrice - isB2B:', this.isB2B, 'unitPriceExclVat:', this.unitPriceExclVat, 'unitPriceInclVat:', this.unitPriceInclVat, 'isCart:', isCart, 'showInclOnly:', showInclOnly);
        
        // Apply discount if we have one and base price
        if (this.currentDiscount > 0 && this.baseUnitPrice > 0) {
          this.applyDiscountToPrice();
        } else {
          // No discount, use prices as extracted
          if (isCart && !showInclOnly) {
            // For cart items (total price column), we need to get the unit price from product-price, not final-line-price
            // The final-line-price is the total, so we'll use that in renderPrice
            // But for now, set unitPrice from product-price if available
            if (this.isB2B) {
              this.unitPrice = this.unitPriceExclVat || this.unitPrice;
              console.log('[DynamicPriceUpdate] Set unitPrice (cart, B2B, excl):', this.unitPrice);
            } else {
              this.unitPrice = this.unitPriceInclVat || this.unitPrice;
              console.log('[DynamicPriceUpdate] Set unitPrice (cart, regular, incl):', this.unitPrice);
            }
          } else {
            // For PDP, collection, and price under title, use unit price
            if (this.isB2B) {
              this.unitPrice = this.unitPriceExclVat || this.unitPrice;
              console.log('[DynamicPriceUpdate] Set unitPrice (B2B, excl):', this.unitPrice);
            } else {
              this.unitPrice = this.unitPriceInclVat || this.unitPrice;
              console.log('[DynamicPriceUpdate] Set unitPrice (regular, incl):', this.unitPrice);
            }
          }
        }
        
        // Fallback: if no B2B prices found, try to extract from any visible text
        if (!this.unitPrice || this.unitPrice === 0) {
          // Try to find any price text in the element
          const allSpans = this.originalPriceElement.querySelectorAll('span');
          for (let span of allSpans) {
            const style = window.getComputedStyle(span);
            // Only check visible spans (not hidden by our CSS)
            if (style.visibility !== 'hidden' && style.display !== 'none' && parseFloat(style.opacity) > 0) {
              const priceText = span.textContent || span.innerText || '';
              const priceMatch = priceText.match(/€?\s*([\d,\.]+)/);
              if (priceMatch) {
                let priceString = priceMatch[1];
                if (priceString.includes('.') && priceString.includes(',')) {
                  priceString = priceString.replace(/\./g, '').replace(',', '.');
                } else {
                  priceString = priceString.replace(',', '.');
                }
                const parsedPrice = parseFloat(priceString);
                if (parsedPrice && parsedPrice > 0) {
                  this.unitPrice = parsedPrice;
                  // Try to determine if it's excl or incl based on text
                  const wasB2B = this.isB2B;
                  if (priceText.toLowerCase().includes('excl')) {
                    this.unitPriceExclVat = parsedPrice;
                    this.isB2B = true;
                  } else if (priceText.toLowerCase().includes('incl')) {
                    this.unitPriceInclVat = parsedPrice;
                    this.isB2B = false;
                  }
                  // If B2B status changed, recalculate originalPrice
                  if (wasB2B !== this.isB2B) {
                    // Recalculate originalPrice with correct BTW status
                    if (this.compareAtPriceExclVat && this.compareAtPriceExclVat > 0) {
                      if (this.isB2B) {
                        this.originalPrice = this.compareAtPriceExclVat; // excl. BTW
                      } else {
                        this.originalPrice = this.compareAtPriceExclVat * 1.21; // incl. BTW
                      }
                    } else {
                      // Try to get from data attribute
                      const compareAtPriceAttr = this.getAttribute('data-compare-at-price');
                      if (compareAtPriceAttr) {
                        const compareAtPriceExclVat = parseInt(compareAtPriceAttr) / 100;
                        this.compareAtPriceExclVat = compareAtPriceExclVat;
                        if (this.isB2B) {
                          this.originalPrice = compareAtPriceExclVat;
                        } else {
                          this.originalPrice = compareAtPriceExclVat * 1.21;
                        }
                      }
                    }
                  }
                  break;
                }
              }
            }
          }
          
          // Last resort: try to get from element's direct text content
          if (!this.unitPrice || this.unitPrice === 0) {
            const directText = this.originalPriceElement.textContent || this.originalPriceElement.innerText || '';
            const priceMatch = directText.match(/€?\s*([\d,\.]+)/);
            if (priceMatch) {
              let priceString = priceMatch[1];
              if (priceString.includes('.') && priceString.includes(',')) {
                priceString = priceString.replace(/\./g, '').replace(',', '.');
              } else {
                priceString = priceString.replace(',', '.');
              }
              const parsedPrice = parseFloat(priceString);
              if (parsedPrice && parsedPrice > 0) {
                this.unitPrice = parsedPrice;
              }
            }
          }
        }

        // NOW that isB2B is determined, get compare_at_price
        // First, try to get compare_at_price from data attribute (for collection pages and PDP)
        // IMPORTANT: isB2B must be determined BEFORE this point (done above)
        // But note: isB2B might be updated in fallback logic below, so we'll recalculate if needed
        // NOTE: The B2B app converts compare_at_price to incl. BTW, so we need to handle this
        const compareAtPriceAttr = this.getAttribute('data-compare-at-price');
        console.log('[DynamicPriceUpdate] compare_at_price attribute:', compareAtPriceAttr, 'isB2B:', this.isB2B);
        if (compareAtPriceAttr && compareAtPriceAttr !== 'null' && compareAtPriceAttr !== '') {
          // Shopify prices are in cents, so divide by 100
          // BUT: The B2B app outputs compare_at_price as incl. BTW, so we need to convert
          const compareAtPriceInclVat = parseInt(compareAtPriceAttr, 10) / 100;
          console.log('[DynamicPriceUpdate] compareAtPriceInclVat (from B2B app, parsed):', compareAtPriceInclVat);
          if (compareAtPriceInclVat > 0 && !isNaN(compareAtPriceInclVat)) {
            // The B2B app outputs compare_at_price as incl. BTW
            // Convert back to excl. BTW for storage
            const compareAtPriceExclVat = compareAtPriceInclVat / 1.21;
            this.compareAtPriceExclVat = compareAtPriceExclVat;
            console.log('[DynamicPriceUpdate] compareAtPriceExclVat (converted back):', compareAtPriceExclVat);
            
            // Now set originalPrice based on user type
            // For B2B: show excl. BTW (convert back)
            // For regular: show incl. BTW (use as-is from B2B app)
            if (this.isB2B) {
              this.originalPrice = compareAtPriceExclVat; // excl. BTW for B2B
              console.log('[DynamicPriceUpdate] Set originalPrice (B2B, excl):', this.originalPrice);
            } else {
              this.originalPrice = compareAtPriceInclVat; // incl. BTW for regular (already incl. from B2B app)
              console.log('[DynamicPriceUpdate] Set originalPrice (regular, incl):', this.originalPrice);
            }
          } else {
            console.warn('[DynamicPriceUpdate] Invalid compareAtPriceInclVat:', compareAtPriceInclVat);
          }
        } else {
          console.log('[DynamicPriceUpdate] No compare_at_price attribute found or empty');
        }
        
        // Try to find compare_at_price (original price) from variant data
        const variantId = this.quantityInput?.getAttribute('data-quantity-variant-id') || 
                         document.querySelector('input[name="id"]')?.value;
        
        // Only try other methods if we don't have compare_at_price from data attribute
        if ((!this.originalPrice || this.originalPrice === 0) && variantId) {
          // Try to get variant from product JSON
          const productId = this.getAttribute('data-product-id');
          const productJson = productId ? document.getElementById(`ProductJSON-${productId}`) : null;
          if (productJson) {
            try {
              const productData = JSON.parse(productJson.textContent);
              const variant = productData.variants?.find(v => v.id === parseInt(variantId));
              if (variant && variant.compare_at_price) {
                // NOTE: The B2B app outputs compare_at_price as incl. BTW in the data attribute
                // But from ProductJSON, it's still excl. BTW (raw Shopify value)
                // So we need to check: if we got it from data-attribute, it's incl., if from JSON, it's excl.
                // Since we're getting it from ProductJSON here, it's excl. BTW
                const compareAtPriceExclVat = variant.compare_at_price / 100; // Shopify prices are in cents
                this.compareAtPriceExclVat = compareAtPriceExclVat; // Store for recalculation
                
                if (this.isB2B) {
                  // For B2B users, compare_at_price is excl. BTW (from JSON)
                  this.originalPrice = compareAtPriceExclVat;
                  console.log('[DynamicPriceUpdate] Got compare_at_price from ProductJSON (excl):', this.originalPrice);
                } else {
                  // For regular users, add BTW (21% in Netherlands)
                  this.originalPrice = compareAtPriceExclVat * 1.21;
                  console.log('[DynamicPriceUpdate] Got compare_at_price from ProductJSON (incl):', this.originalPrice);
                }
              }
              // Also update unit price from variant if B2B price not found
              if (!this.unitPrice && variant.price) {
                this.unitPrice = variant.price / 100;
              }
            } catch (e) {
              console.warn('DynamicPriceUpdate: Could not parse product JSON', e);
            }
          }
        }
        
        // Also try to get compare_at_price from variant-selects element (only if not already set)
        // This is important for PDP when variant changes
        if (!this.originalPrice || this.originalPrice === 0) {
          // Try to get from variant-selects [data-selected-variant] JSON
          const variantSelects = this.closest('.product__block--price')?.querySelector('variant-selects') ||
                                document.querySelector('variant-selects');
          if (variantSelects) {
            try {
              const selectedVariantEl = variantSelects.querySelector('[data-selected-variant]');
              if (selectedVariantEl && selectedVariantEl.textContent) {
                const variantData = JSON.parse(selectedVariantEl.textContent);
                if (variantData && variantData.compare_at_price) {
                  // From variant-selects JSON, compare_at_price is excl. BTW (raw Shopify value)
                  const compareAtPriceExclVat = variantData.compare_at_price / 100; // Shopify prices are in cents
                  this.compareAtPriceExclVat = compareAtPriceExclVat; // Store for recalculation
                  if (this.isB2B) {
                    this.originalPrice = compareAtPriceExclVat;
                    console.log('[DynamicPriceUpdate] Got compare_at_price from variant-selects (excl):', this.originalPrice);
                  } else {
                    this.originalPrice = compareAtPriceExclVat * 1.21; // Add 21% BTW
                    console.log('[DynamicPriceUpdate] Got compare_at_price from variant-selects (incl):', this.originalPrice);
                  }
                }
              }
            } catch (e) {
              console.warn('DynamicPriceUpdate: Could not get compare_at_price from variant-selects', e);
            }
          }
          
          // Also try from select options with data attributes
          if (!this.originalPrice || this.originalPrice === 0) {
            const variantSelect = this.closest('.product__block--price')?.querySelector('variant-selects select') ||
                                document.querySelector('variant-selects select');
            if (variantSelect) {
              try {
                const selectedOption = variantSelect.querySelector('option:checked') || 
                                     variantSelect.querySelector(`option[value="${variantId}"]`);
                if (selectedOption && selectedOption.dataset.compare_at_price) {
                  // From select option data attribute, it might be incl. BTW (if set by B2B app) or excl. (if raw Shopify)
                  // We'll assume it's incl. BTW (like the data-compare-at-price attribute) since B2B app might modify it
                  const compareAtPriceInclVat = parseInt(selectedOption.dataset.compare_at_price, 10) / 100;
                  const compareAtPriceExclVat = compareAtPriceInclVat / 1.21; // Convert back to excl.
                  this.compareAtPriceExclVat = compareAtPriceExclVat; // Store for recalculation
                  if (this.isB2B) {
                    this.originalPrice = compareAtPriceExclVat;
                    console.log('[DynamicPriceUpdate] Got compare_at_price from select option (excl):', this.originalPrice);
                  } else {
                    this.originalPrice = compareAtPriceInclVat; // incl. BTW (as provided)
                    console.log('[DynamicPriceUpdate] Got compare_at_price from select option (incl):', this.originalPrice);
                  }
                }
              } catch (e) {
                console.warn('DynamicPriceUpdate: Could not get compare_at_price from select options', e);
              }
            }
          }
        }
        
        // FINAL STEP: Recalculate originalPrice at the end to ensure it uses the final isB2B status
        // This is important because isB2B might have been updated in fallback logic
        // NOTE: The B2B app outputs compare_at_price as incl. BTW, so we need to convert
        console.log('[DynamicPriceUpdate] FINAL STEP - compareAtPriceExclVat:', this.compareAtPriceExclVat, 'isB2B:', this.isB2B);
        if (this.compareAtPriceExclVat && this.compareAtPriceExclVat > 0) {
          if (this.isB2B) {
            this.originalPrice = this.compareAtPriceExclVat; // excl. BTW for B2B
            console.log('[DynamicPriceUpdate] FINAL - Set originalPrice (B2B, excl):', this.originalPrice);
          } else {
            // For regular users, convert excl. back to incl. (B2B app outputs as incl.)
            this.originalPrice = this.compareAtPriceExclVat * 1.21; // incl. BTW for regular
            console.log('[DynamicPriceUpdate] FINAL - Set originalPrice (regular, incl):', this.originalPrice);
          }
        } else {
          // Try to get from data attribute one more time
          const compareAtPriceAttr = this.getAttribute('data-compare-at-price');
          console.log('[DynamicPriceUpdate] FINAL - Retry compare_at_price attribute:', compareAtPriceAttr);
          if (compareAtPriceAttr && compareAtPriceAttr !== 'null' && compareAtPriceAttr !== '') {
            // B2B app outputs as incl. BTW, so convert back to excl. for storage
            const compareAtPriceInclVat = parseInt(compareAtPriceAttr, 10) / 100;
            if (compareAtPriceInclVat > 0 && !isNaN(compareAtPriceInclVat)) {
              const compareAtPriceExclVat = compareAtPriceInclVat / 1.21;
              this.compareAtPriceExclVat = compareAtPriceExclVat;
              if (this.isB2B) {
                this.originalPrice = compareAtPriceExclVat; // excl. BTW for B2B
                console.log('[DynamicPriceUpdate] FINAL - Retry Set originalPrice (B2B, excl):', this.originalPrice);
              } else {
                this.originalPrice = compareAtPriceInclVat; // incl. BTW for regular (already incl. from B2B app)
                console.log('[DynamicPriceUpdate] FINAL - Retry Set originalPrice (regular, incl):', this.originalPrice);
              }
            }
          }
        }
        console.log('[DynamicPriceUpdate] After updatePriceFromB2BElement - unitPrice:', this.unitPrice, 'originalPrice:', this.originalPrice, 'unitPriceExclVat:', this.unitPriceExclVat, 'unitPriceInclVat:', this.unitPriceInclVat);
      }

      handleQuantityChange(event) {
        const newQuantity = parseInt(event.target.value) || 1;
        console.log('[DynamicPriceUpdate] Quantity changed - old:', this.quantity, 'new:', newQuantity);
        if (newQuantity !== this.quantity) {
          this.quantity = newQuantity;
          
          // Check if quantity falls into a different quantity break tier
          this.updatePriceForQuantityBreak(newQuantity);
          
          // For cart items, reset the cached unit price from dynamic element
          // so it gets re-fetched with the new quantity/staffelkorting
          const isCart = this.hasAttribute('data-is-cart') || 
                        this.getAttribute('data-is-cart') === 'true';
          const showInclOnly = this.hasAttribute('data-show-incl-only') || 
                             this.getAttribute('data-show-incl-only') === 'true';
          if (isCart && !showInclOnly) {
            this.cartUnitPriceFromDynamicElement = 0; // Reset to force re-fetch
          }
          
          // For cart items, we need to re-fetch the price from B2B app as it might have changed
          // The B2B app will update the final-line-price with the new total
          this.updatePriceFromB2BElement(); // Re-check price in case B2B app updated it
          this.renderPrice();
        }
      }

      handleVariantChange(event) {
        // When variant changes, update price from B2B element
        // B2B app might need more time to update
        setTimeout(() => {
          // Reset prices so they get recalculated for new variant
          this.originalPrice = 0;
          this.compareAtPriceExclVat = 0;
          this.unitPrice = 0;
          this.unitPriceExclVat = 0;
          this.unitPriceInclVat = 0;
          
          // Try to update data-compare-at-price attribute from new variant
          const productId = this.getAttribute('data-product-id');
          if (productId) {
            // Try to get variant from ProductJSON
            const productJson = document.getElementById(`ProductJSON-${productId}`);
            if (productJson) {
              try {
                const productData = JSON.parse(productJson.textContent);
                // Get current selected variant ID from variant-selects
                const variantSelects = document.querySelector('variant-selects');
                if (variantSelects) {
                  const selectedVariantEl = variantSelects.querySelector('[data-selected-variant]');
                  if (selectedVariantEl && selectedVariantEl.textContent) {
                    const variantData = JSON.parse(selectedVariantEl.textContent);
                    if (variantData && variantData.compare_at_price) {
                      // Update the data-compare-at-price attribute
                      this.setAttribute('data-compare-at-price', variantData.compare_at_price);
                    }
                  }
                }
              } catch (e) {
                console.warn('DynamicPriceUpdate: Could not update compare_at_price from variant change', e);
              }
            }
          }
          
          this.updatePriceFromB2BElement();
          this.renderPrice();
        }, 200); // Delay to let B2B app update first
      }

      // Watch for B2B price updates using MutationObserver
      observeB2BPriceChanges() {
        if (!this.originalPriceElement) {
          console.warn('[DynamicPriceUpdate] Cannot observe - no originalPriceElement');
          return;
        }

        const observer = new MutationObserver((mutations) => {
          let shouldUpdate = false;
          
          mutations.forEach((mutation) => {
            // Check if any text content was added or changed
            if (mutation.type === 'childList' || mutation.type === 'characterData') {
              const target = mutation.target;
              // Check if the mutation is in a price-related element
              if (target.classList?.contains('bss-b2b-price-no-vat') || 
                  target.classList?.contains('bss-b2b-price-has-vat') ||
                  target.hasAttribute('bss-b2b-final-line-price') ||
                  target.hasAttribute('bss-b2b-product-price') ||
                  target.textContent?.match(/€|[\d,\.]+/)) {
                shouldUpdate = true;
                console.log('[DynamicPriceUpdate] Mutation detected in price element:', target);
              }
            }
          });
          
          if (shouldUpdate) {
            console.log('[DynamicPriceUpdate] Price mutation detected, updating...');
            const oldPrice = this.unitPrice;
            const oldOriginalPrice = this.originalPrice;
            this.updatePriceFromB2BElement();
            // Re-render if price changed or if we need to update original price
            if (oldPrice !== this.unitPrice || oldOriginalPrice !== this.originalPrice || !this.unitPrice || this.unitPrice === 0) {
              this.renderPrice();
            }
          }
        });

        observer.observe(this.originalPriceElement, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['style', 'class']
        });

        this.priceObserver = observer;
        console.log('[DynamicPriceUpdate] Started observing B2B price changes on:', this.originalPriceElement);
      }

      formatPrice(price) {
        // Format price in Dutch format: €X,XX
        return `€${price.toFixed(2).replace('.', ',')}`;
      }

      // Observe the dynamic-price element under title for cart total price updates
      observeCartUnitPriceElement() {
        const cartRow = this.closest('tr') || this.closest('.cart-item');
        if (!cartRow) return;
        
        const priceUnderTitleElement = cartRow.querySelector('dynamic-price-update[data-show-incl-only="true"]');
        if (!priceUnderTitleElement) {
          // Try again after a delay in case it's not loaded yet
          setTimeout(() => {
            this.observeCartUnitPriceElement();
          }, 1000);
          return;
        }
        
        const observer = new MutationObserver(() => {
          // When the unit price under title changes, update our total price
          // Try .dynamic-price__current first
          let unitPriceSpan = priceUnderTitleElement.querySelector('.dynamic-price__current');
          let priceText = '';
          
          if (unitPriceSpan && unitPriceSpan.textContent && unitPriceSpan.textContent.trim()) {
            priceText = unitPriceSpan.textContent.trim();
          } else {
            // Fallback: get price from B2B app elements within the dynamic-price element
            const mainDiv = priceUnderTitleElement.querySelector('.dynamic-price__main');
            if (mainDiv) {
              const b2bPriceExcl = mainDiv.querySelector('.bss-b2b-price-no-vat');
              const b2bPriceIncl = mainDiv.querySelector('.bss-b2b-price-has-vat');
              
              if (b2bPriceExcl && b2bPriceExcl.textContent) {
                priceText = b2bPriceExcl.textContent.trim();
              } else if (b2bPriceIncl && b2bPriceIncl.textContent) {
                priceText = b2bPriceIncl.textContent.trim();
              }
            }
          }
          
          if (priceText) {
            const priceMatch = priceText.match(/€?\s*([\d,\.]+)/);
            if (priceMatch) {
              let priceString = priceMatch[1];
              if (priceString.includes('.') && priceString.includes(',')) {
                priceString = priceString.replace(/\./g, '').replace(',', '.');
              } else {
                priceString = priceString.replace(',', '.');
              }
              const extractedUnitPrice = parseFloat(priceString) || 0;
              if (extractedUnitPrice > 0 && extractedUnitPrice !== this.cartUnitPriceFromDynamicElement) {
                this.cartUnitPriceFromDynamicElement = extractedUnitPrice;
                console.log('[DynamicPriceUpdate] Cart unit price updated from dynamic element:', extractedUnitPrice);
                // Re-render the total price
                this.renderPrice();
              }
            }
          }
        });
        
        observer.observe(priceUnderTitleElement, {
          childList: true,
          subtree: true,
          characterData: true
        });
        
        this.cartUnitPriceObserver = observer;
        console.log('[DynamicPriceUpdate] Started observing cart unit price element');
      }

      // Initialize quantity break table handling
      initQuantityBreakTable() {
        // Only on product pages (not collection, cart, etc.)
        const isCollection = this.hasAttribute('data-is-collection') || 
                           this.getAttribute('data-is-collection') === 'true';
        const isCart = this.hasAttribute('data-is-cart') || 
                      this.getAttribute('data-is-cart') === 'true';
        
        if (isCollection || isCart) {
          return; // Don't handle quantity breaks on collection or cart pages
        }

        // Find the quantity break table
        const productInfo = this.closest('#ProductInfo-') || 
                           this.closest('.product__info-container') ||
                           document.querySelector('#ProductInfo-') ||
                           document.querySelector('.product__info-container');
        
        if (productInfo) {
          this.quantityBreakTable = productInfo.querySelector('.quantity_break_table');
          
          if (this.quantityBreakTable) {
            console.log('[DynamicPriceUpdate] Found quantity break table');
            
            // Store base price before any discounts (will be set when prices are loaded)
            
            // Add click listeners to table rows
            const rows = this.quantityBreakTable.querySelectorAll('.bss-b2b-qty-table-row');
            rows.forEach((row) => {
              row.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleQuantityBreakRowClick(row);
              });
              
              // Make rows look clickable
              row.style.cursor = 'pointer';
            });
            
            // Check initial quantity and update active row
            this.updatePriceForQuantityBreak(this.quantity);
          } else {
            // Table might not be loaded yet, try again after a delay
            setTimeout(() => {
              this.initQuantityBreakTable();
            }, 1000);
          }
        }
      }

      // Handle click on quantity break table row
      handleQuantityBreakRowClick(row) {
        console.log('[DynamicPriceUpdate] Quantity break row clicked:', row);
        
        // Remove active class from all rows
        const allRows = this.quantityBreakTable.querySelectorAll('.bss-b2b-qty-table-row');
        allRows.forEach((r) => r.classList.remove('qty-row-active'));
        
        // Add active class to clicked row
        row.classList.add('qty-row-active');
        
        // Get discount percentage from the row
        const discountCell = row.querySelector('td:last-child');
        if (discountCell) {
          const discountText = discountCell.textContent.trim();
          const discountMatch = discountText.match(/(\d+)%/);
          if (discountMatch) {
            const discount = parseInt(discountMatch[1], 10);
            this.currentDiscount = discount;
            console.log('[DynamicPriceUpdate] Applied discount from row click:', discount + '%');
            
            // Get min quantity from row and update quantity input if needed
            const minQty = parseInt(row.getAttribute('data-min'), 10);
            if (minQty && this.quantityInput) {
              // Update quantity if it's below the minimum for this tier
              if (parseInt(this.quantityInput.value, 10) < minQty) {
                this.quantityInput.value = minQty;
                this.quantity = minQty;
                console.log('[DynamicPriceUpdate] Updated quantity to minimum for tier:', minQty);
              } else {
                // Re-read current quantity from input
                this.quantity = parseInt(this.quantityInput.value, 10) || 1;
                console.log('[DynamicPriceUpdate] Using current quantity:', this.quantity);
              }
              
              // Trigger change event on quantity input to ensure all handlers run
              const changeEvent = new Event('change', { bubbles: true });
              this.quantityInput.dispatchEvent(changeEvent);
              
              // Also trigger input event for real-time updates
              const inputEvent = new Event('input', { bubbles: true });
              this.quantityInput.dispatchEvent(inputEvent);
            } else {
              // No quantity input, just recalculate with current quantity
              this.quantity = parseInt(this.quantityInput?.value, 10) || this.quantity || 1;
            }
            
            // Recalculate and update price
            this.applyDiscountToPrice();
            this.renderPrice();
          }
        }
      }

      // Update price based on quantity break tier
      updatePriceForQuantityBreak(quantity) {
        if (!this.quantityBreakTable) {
          return; // No quantity break table available
        }
        
        // Find the row that matches this quantity
        const rows = this.quantityBreakTable.querySelectorAll('.bss-b2b-qty-table-row');
        let matchingRow = null;
        
        for (const row of rows) {
          const minQty = parseInt(row.getAttribute('data-min'), 10);
          const maxQty = parseInt(row.getAttribute('data-max'), 10);
          
          // if (quantity >= minQty && quantity <= maxQty) {
          //   matchingRow = row;
          //   break;
          // }

          const isMatch = minQty === maxQty  ? quantity >= minQty : quantity >= minQty && quantity <= maxQty;

          if (isMatch) {
            matchingRow = row;
            break;
          }
        }
        
        if (matchingRow) {
          // Remove active class from all rows
          rows.forEach((r) => r.classList.remove('qty-row-active'));
          
          // Add active class to matching row
          matchingRow.classList.add('qty-row-active');
          
          // Get discount percentage
          const discountCell = matchingRow.querySelector('td:last-child');
          if (discountCell) {
            const discountText = discountCell.textContent.trim();
            const discountMatch = discountText.match(/(\d+)%/);
            if (discountMatch) {
              const discount = parseInt(discountMatch[1], 10);
              
              // Only update if discount changed
              if (this.currentDiscount !== discount) {
                this.currentDiscount = discount;
                console.log('[DynamicPriceUpdate] Quantity', quantity, 'falls into tier with discount:', discount + '%');
                
                // Recalculate and update price
                this.applyDiscountToPrice();
              }
            }
          }
        } else {
          // No matching row found - reset discount to 0
          if (this.currentDiscount > 0) {
            console.log('[DynamicPriceUpdate] Quantity', quantity, 'does not match any tier, resetting discount');
            this.currentDiscount = 0;
            this.applyDiscountToPrice();
          }
        }
      }

      // Apply discount to base price
      applyDiscountToPrice() {
        // Store base price if not already stored (use excl. BTW as base)
        if (!this.baseUnitPrice || this.baseUnitPrice === 0) {
          // Try to get base price from current prices
          if (this.unitPriceExclVat > 0) {
            // If we already have a discount, we need to reverse it to get base
            if (this.currentDiscount > 0) {
              this.baseUnitPrice = this.unitPriceExclVat / ((100 - this.currentDiscount) / 100);
            } else {
              this.baseUnitPrice = this.unitPriceExclVat;
            }
          } else if (this.unitPriceInclVat > 0) {
            // Convert incl. to excl. for base
            let inclPrice = this.unitPriceInclVat;
            if (this.currentDiscount > 0) {
              inclPrice = this.unitPriceInclVat / ((100 - this.currentDiscount) / 100);
            }
            this.baseUnitPrice = inclPrice / 1.21;
          } else if (this.unitPrice > 0) {
            // Use current unitPrice as base (might need conversion)
            if (this.isB2B) {
              if (this.currentDiscount > 0) {
                this.baseUnitPrice = this.unitPrice / ((100 - this.currentDiscount) / 100);
              } else {
                this.baseUnitPrice = this.unitPrice;
              }
            } else {
              // Regular customer, convert incl. to excl.
              let inclPrice = this.unitPrice;
              if (this.currentDiscount > 0) {
                inclPrice = this.unitPrice / ((100 - this.currentDiscount) / 100);
              }
              this.baseUnitPrice = inclPrice / 1.21;
            }
          }
        }
        
        if (this.baseUnitPrice > 0 && this.currentDiscount > 0) {
          // Calculate discounted price
          const discountMultiplier = (100 - this.currentDiscount) / 100;
          
          // Apply discount to base price (excl. BTW)
          const discountedExclVat = this.baseUnitPrice * discountMultiplier;
          
          // Calculate discounted incl. BTW price
          const discountedInclVat = discountedExclVat * 1.21;
          
          // Update prices
          this.unitPriceExclVat = discountedExclVat;
          this.unitPriceInclVat = discountedInclVat;
          
          // Update unitPrice based on B2B status
          if (this.isB2B) {
            this.unitPrice = discountedExclVat;
          } else {
            this.unitPrice = discountedInclVat;
          }
          
          console.log('[DynamicPriceUpdate] Applied discount - base:', this.baseUnitPrice, 'discount:', this.currentDiscount + '%', 'new excl:', discountedExclVat, 'new incl:', discountedInclVat, 'unitPrice:', this.unitPrice);
        } else if (this.currentDiscount === 0 && this.baseUnitPrice > 0) {
          // No discount, restore base prices
          this.unitPriceExclVat = this.baseUnitPrice;
          this.unitPriceInclVat = this.baseUnitPrice * 1.21;
          
          if (this.isB2B) {
            this.unitPrice = this.unitPriceExclVat;
          } else {
            this.unitPrice = this.unitPriceInclVat;
          }
          
          console.log('[DynamicPriceUpdate] No discount - restored base prices');
        }
      }

      renderPrice() {
        console.log('[DynamicPriceUpdate] renderPrice called');
        // Check if this is a cart item (total price column)
        const isCart = this.hasAttribute('data-is-cart') || 
                      this.getAttribute('data-is-cart') === 'true';
        const showInclOnly = this.hasAttribute('data-show-incl-only') || 
                           this.getAttribute('data-show-incl-only') === 'true';
        
        // If no unit price, try to get it again
        if (!this.unitPrice || this.unitPrice === 0) {
          console.log('[DynamicPriceUpdate] No unitPrice, calling updatePriceFromB2BElement');
          this.updatePriceFromB2BElement();
        }
        
        // For cart items (total price column), unitPrice might be 0 but we still need to render
        // because the price comes from final-line-price which is already the total
        // Don't return early for cart items - let them try to render anyway
        if ((!this.unitPrice || this.unitPrice === 0) && !(isCart && !showInclOnly)) {
          console.warn('[DynamicPriceUpdate] Still no unitPrice after update, returning early');
          return;
        }
        
        // For cart items, if unitPrice is 0, try to get it from the B2B element again
        if (isCart && !showInclOnly && (!this.unitPrice || this.unitPrice === 0)) {
          console.log('[DynamicPriceUpdate] Cart item - unitPrice is 0, trying to re-fetch from B2B element');
          // Force re-extraction of price
          const finalLinePriceElement = this.originalPriceElement?.querySelector('[bss-b2b-final-line-price]') ||
                                       this.originalPriceElement;
          if (finalLinePriceElement) {
            const priceExclVatElement = finalLinePriceElement.querySelector('.bss-b2b-price-no-vat');
            const priceInclVatElement = finalLinePriceElement.querySelector('.bss-b2b-price-has-vat');
            
            if (priceExclVatElement || priceInclVatElement) {
              // Re-extract prices
              if (priceExclVatElement) {
                const priceText = priceExclVatElement.textContent || priceExclVatElement.innerText || '';
                const priceMatch = priceText.match(/€?\s*([\d,\.]+)/);
                if (priceMatch) {
                  let priceString = priceMatch[1];
                  if (priceString.includes('.') && priceString.includes(',')) {
                    priceString = priceString.replace(/\./g, '').replace(',', '.');
                  } else {
                    priceString = priceString.replace(',', '.');
                  }
                  this.unitPriceExclVat = parseFloat(priceString) || 0;
                }
              }
              
              if (priceInclVatElement) {
                const priceText = priceInclVatElement.textContent || priceInclVatElement.innerText || '';
                const priceMatch = priceText.match(/€?\s*([\d,\.]+)/);
                if (priceMatch) {
                  let priceString = priceMatch[1];
                  if (priceString.includes('.') && priceString.includes(',')) {
                    priceString = priceString.replace(/\./g, '').replace(',', '.');
                  } else {
                    priceString = priceString.replace(',', '.');
                  }
                  this.unitPriceInclVat = parseFloat(priceString) || 0;
                }
              }
              
              // Set unitPrice based on B2B status (this is actually total for cart items)
              if (this.isB2B) {
                this.unitPrice = this.unitPriceExclVat || 0;
              } else {
                this.unitPrice = this.unitPriceInclVat || 0;
              }
              
              console.log('[DynamicPriceUpdate] Re-extracted cart price - unitPrice:', this.unitPrice, 'unitPriceExclVat:', this.unitPriceExclVat, 'unitPriceInclVat:', this.unitPriceInclVat);
            }
          }
        }

        // Make sure originalPrice is also updated if not set
        // But don't overwrite if it's already set correctly
        // IMPORTANT: Don't call updatePriceFromB2BElement again here if we already called it above
        // It might reset the prices we just extracted
        // Only update if we don't have prices yet
        if ((!this.unitPrice || this.unitPrice === 0) && (!this.unitPriceExclVat || this.unitPriceExclVat === 0) && (!this.unitPriceInclVat || this.unitPriceInclVat === 0)) {
          const oldOriginalPrice = this.originalPrice;
          this.updatePriceFromB2BElement();
          
          // If originalPrice was set but got reset, restore it
          // This prevents overwriting when updatePriceFromB2BElement runs multiple times
          if (oldOriginalPrice && oldOriginalPrice > 0 && (!this.originalPrice || this.originalPrice === 0)) {
            // Only restore if we're sure it was correct
            // Actually, let updatePriceFromB2BElement recalculate it properly
          }
        }

        // Calculate total price
        // For cart items (not price under title), we need to calculate: unitPrice * quantity
        // The B2B app's final-line-price has rounding errors, so we use product-price (unit) * quantity
        // Note: isCart and showInclOnly are already defined above
        
        if (isCart && !showInclOnly) {
          // For cart items (total price column), calculate: unitPrice * quantity
          // Use the unit price from dynamic-price element under title if available (has correct staffelkorting price)
          // Otherwise use the unitPrice we extracted from product-price
          let unitPriceToUse = this.unitPrice;
          
          if (this.cartUnitPriceFromDynamicElement && this.cartUnitPriceFromDynamicElement > 0) {
            // Use the unit price from the dynamic-price element under title (correct after staffelkorting)
            unitPriceToUse = this.cartUnitPriceFromDynamicElement;
            console.log('[DynamicPriceUpdate] Cart item total - using unit price from dynamic element:', unitPriceToUse);
          } else {
            // Try to get it again from the dynamic-price element under title
            const cartRow = this.closest('tr') || this.closest('.cart-item');
            if (cartRow) {
              const priceUnderTitleElement = cartRow.querySelector('dynamic-price-update[data-show-incl-only="true"]');
              if (priceUnderTitleElement) {
                // Try .dynamic-price__current first
                let unitPriceSpan = priceUnderTitleElement.querySelector('.dynamic-price__current');
                let priceText = '';
                
                if (unitPriceSpan && unitPriceSpan.textContent && unitPriceSpan.textContent.trim()) {
                  priceText = unitPriceSpan.textContent.trim();
                } else {
                  // Fallback: get price from B2B app elements within the dynamic-price element
                  const mainDiv = priceUnderTitleElement.querySelector('.dynamic-price__main');
                  if (mainDiv) {
                    const b2bPriceExcl = mainDiv.querySelector('.bss-b2b-price-no-vat');
                    const b2bPriceIncl = mainDiv.querySelector('.bss-b2b-price-has-vat');
                    
                    if (b2bPriceExcl && b2bPriceExcl.textContent) {
                      priceText = b2bPriceExcl.textContent.trim();
                    } else if (b2bPriceIncl && b2bPriceIncl.textContent) {
                      priceText = b2bPriceIncl.textContent.trim();
                    }
                  }
                }
                
                if (priceText) {
                  const priceMatch = priceText.match(/€?\s*([\d,\.]+)/);
                  if (priceMatch) {
                    let priceString = priceMatch[1];
                    if (priceString.includes('.') && priceString.includes(',')) {
                      priceString = priceString.replace(/\./g, '').replace(',', '.');
                    } else {
                      priceString = priceString.replace(',', '.');
                    }
                    const extractedUnitPrice = parseFloat(priceString) || 0;
                    if (extractedUnitPrice > 0) {
                      unitPriceToUse = extractedUnitPrice;
                      this.cartUnitPriceFromDynamicElement = extractedUnitPrice;
                      console.log('[DynamicPriceUpdate] Cart item total - extracted unit price from dynamic element:', unitPriceToUse);
                    }
                  }
                }
              }
            }
          }
          
          this.currentPrice = unitPriceToUse * this.quantity;
          console.log('[DynamicPriceUpdate] Cart item total - calculated: unitPrice:', unitPriceToUse, 'quantity:', this.quantity, 'total:', this.currentPrice);
          
          // Round to 2 decimal places to avoid floating point errors
          this.currentPrice = Math.round(this.currentPrice * 100) / 100;
          console.log('[DynamicPriceUpdate] Cart item total - rounded:', this.currentPrice);
        } else {
          // For PDP, calculate: unitPrice * quantity (total price)
          // For price under title, we show unit price only
          if (showInclOnly) {
            // Price under title shows unit price only
            this.currentPrice = this.unitPrice;
            console.log('[DynamicPriceUpdate] Price under title - showing unit price only:', this.currentPrice);
          } else {
            // PDP main price shows total: unitPrice * quantity
            this.currentPrice = this.unitPrice * this.quantity;
            console.log('[DynamicPriceUpdate] PDP - Calculating total - unitPrice:', this.unitPrice, 'quantity:', this.quantity, 'total:', this.currentPrice);
          }
        }

        // Get or create price elements
        if (!this.currentPriceSpan) {
          this.currentPriceSpan = this.querySelector('.dynamic-price__current');
        }
        if (!this.originalPriceSpan) {
          this.originalPriceSpan = this.querySelector('.dynamic-price__original');
        }
        if (!this.unitPriceSpan) {
          this.unitPriceSpan = this.querySelector('.dynamic-price__unit');
        }
        
        // showInclOnly is already defined above, reuse it

        // Update current price
        if (this.currentPriceSpan) {
          // For price under title, show unit price (incl. or excl. BTW based on customer type)
          if (showInclOnly && this.currentPriceSpan) {
            // For cart items under title, show unit price based on customer type
            let unitPriceToShow;
            
            if (this.isB2B) {
              // B2B customers see excl. BTW
              unitPriceToShow = this.unitPriceExclVat || this.unitPrice;
              // If we only have incl. price, convert back to excl.
              if (!unitPriceToShow && this.unitPriceInclVat) {
                unitPriceToShow = this.unitPriceInclVat / 1.21;
              }
            } else {
              // Regular customers see incl. BTW
              unitPriceToShow = this.unitPriceInclVat || this.unitPrice;
              // If we only have excl. price, convert to incl.
              if (!unitPriceToShow && this.unitPriceExclVat) {
                unitPriceToShow = this.unitPriceExclVat * 1.21;
              }
              // If we have unitPrice and user is B2B (shouldn't happen here, but just in case), convert excl. to incl.
              if (!unitPriceToShow && this.unitPrice && this.isB2B) {
                unitPriceToShow = this.unitPrice * 1.21;
              }
            }
            
            console.log('[DynamicPriceUpdate] Price under title - unitPriceToShow:', unitPriceToShow, 'unitPrice:', this.unitPrice, 'unitPriceExclVat:', this.unitPriceExclVat, 'unitPriceInclVat:', this.unitPriceInclVat, 'isB2B:', this.isB2B);
            this.currentPriceSpan.textContent = this.formatPrice(unitPriceToShow || this.unitPrice);
          } else {
            // For cart items (total price column) and PDP, show the calculated currentPrice
            // On PDP, currentPrice is already quantity * unitPrice (total)
            // On cart, currentPrice is already the total from B2B app
            console.log('[DynamicPriceUpdate] Setting currentPriceSpan text to:', this.currentPrice, '(unitPrice:', this.unitPrice, 'quantity:', this.quantity, 'total:', this.currentPrice + ')');
            this.currentPriceSpan.textContent = this.formatPrice(this.currentPrice);
          }
          this.currentPriceSpan.style.visibility = 'visible';
          this.currentPriceSpan.style.opacity = '1';
          this.currentPriceSpan.style.display = 'inline-block';
        } else {
          console.warn('[DynamicPriceUpdate] currentPriceSpan not found', this);
        }

        // Update original price (if available and different from current)
          // For cart items, data-compare-at-price is already the total (compare_at_price * quantity)
          // For PDP, originalPrice is the unit price, so we need to multiply by quantity for total
          // isCart is already defined at the beginning of renderPrice()
        
        // Don't show original price for price under title
        if (showInclOnly) {
          // Skip original price display for price under title
          if (this.originalPriceSpan) {
            this.originalPriceSpan.style.display = 'none';
          }
          
          // Update tax label for price under title (based on customer type)
          const taxLabel = this.querySelector('.dynamic-price__tax');
          if (taxLabel) {
            taxLabel.textContent = this.isB2B ? 'excl. btw' : 'incl. btw';
          }
          
          // Don't return early - we still need to update the current price and tax label
          // Just skip the original price logic below
        }
        
        console.log('[DynamicPriceUpdate] renderPrice - originalPrice:', this.originalPrice, 'unitPrice:', this.unitPrice, 'quantity:', this.quantity, 'isB2B:', this.isB2B, 'isCart:', isCart, 'currentPrice:', this.currentPrice);
        
        if (this.originalPrice && this.originalPrice > 0) {
          let originalTotal;
          
          // For PDP, originalPrice is unit price, so multiply by quantity for total
          // For cart items, originalPrice is already total
          if (!isCart && !showInclOnly) {
            // PDP: multiply original unit price by quantity
            originalTotal = this.originalPrice * this.quantity;
            console.log('[DynamicPriceUpdate] PDP - originalTotal (unit * quantity):', originalTotal, 'originalPrice (unit):', this.originalPrice, 'quantity:', this.quantity);
          } else {
            // Cart: originalPrice is already total
            originalTotal = this.originalPrice;
            console.log('[DynamicPriceUpdate] Cart - originalTotal (already total):', originalTotal);
          }
          
          // Compare original total with current total price
          const shouldShow = originalTotal > this.currentPrice;
          console.log('[DynamicPriceUpdate] Comparing prices - originalTotal:', originalTotal, 'currentPrice:', this.currentPrice, 'shouldShow:', shouldShow);
          
          if (isCart) {
            // For cart items, use vertical layout
            
            if (shouldShow) {
              // For cart items, find the original-row container
              const originalRow = this.querySelector('.dynamic-price__original-row');
              if (originalRow && this.originalPriceSpan) {
                console.log('[DynamicPriceUpdate] Showing original price (cart total):', originalTotal);
                this.originalPriceSpan.textContent = this.formatPrice(originalTotal);
                this.originalPriceSpan.style.display = 'block';
                this.originalPriceSpan.style.visibility = 'visible';
                this.originalPriceSpan.style.opacity = '1';
                originalRow.style.display = 'flex';
              } else if (this.originalPriceSpan) {
                // Fallback for non-vertical layout
                this.originalPriceSpan.textContent = this.formatPrice(originalTotal);
                this.originalPriceSpan.style.display = 'inline';
                this.originalPriceSpan.style.visibility = 'visible';
                this.originalPriceSpan.style.opacity = '1';
              }
            } else {
              const originalRow = this.querySelector('.dynamic-price__original-row');
              if (originalRow) {
                originalRow.style.display = 'none';
              }
              if (this.originalPriceSpan) {
                this.originalPriceSpan.style.display = 'none';
              }
            }
          } else {
            // For PDP, show original total price (unit * quantity)
            if (this.originalPriceSpan) {
              console.log('[DynamicPriceUpdate] Showing original price (PDP total):', originalTotal);
              this.originalPriceSpan.textContent = this.formatPrice(originalTotal);
              this.originalPriceSpan.style.display = 'inline';
              this.originalPriceSpan.style.visibility = 'visible';
              this.originalPriceSpan.style.opacity = '1';
            }
          }
        } else {
          console.log('[DynamicPriceUpdate] No originalPrice available - hiding');
          if (this.originalPriceSpan) {
            // No original price available - hide it
            this.originalPriceSpan.style.display = 'none';
          }
        }

        // Update unit price (only if element exists - not on collection/cart)
        if (this.unitPriceSpan) {
          this.unitPriceSpan.textContent = this.formatPrice(this.unitPrice);
        }

        // Update tax label (if not already updated for price under title)
        if (!showInclOnly) {
          const taxLabel = this.querySelector('.dynamic-price__tax');
          if (taxLabel) {
            taxLabel.textContent = this.isB2B ? 'excl. btw' : 'incl. btw';
          }
        }
      }
    }
  );
}








